import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { validateEmail } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { router } from 'expo-router';
import { useState } from 'react';
import { View } from 'react-native';
import { AuthLink } from './components/auth-link';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('FORGOT_PASSWORD');

export function ForgotPasswordScreen() {
  const { forgetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSendResetLink = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await forgetPassword(email.trim());

      if (result.error) {
        logger.error('Forgot password failed:', result.error);
        // A delivery failure is not the address's fault. Writing it to errors.email
        // turned the field red and told the user their email was malformed.
        toast.error('Could not send the reset link', {
          description: 'Something went wrong on our end. Try again in a moment.',
        });
      } else {
        logger.log('Password reset email sent successfully');
        setIsSuccess(true);
      }
    } catch {
      toast.error('Could not send the reset link', {
        description: 'Check your connection and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthScreenLayout
        title="Forgot Password"
        heading="Check your email"
        subheading="We've sent a password reset link to:"
      >
        <View className="gap-4 items-center">
          <Text className="text-base font-semibold text-center text-warm-gray-100">
            {email}
          </Text>
          <Text className="text-sm text-center text-warm-gray-300">
            Open it and follow the link to choose a new password.
          </Text>
          <Button
            size="lg"
            fullWidth
            className="mt-2"
            onPress={() => router.push({ pathname: '/(auth)/login' })}
            testID="forgot-success-login"
          >
            Back to log in
          </Button>
        </View>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout
      title="Forgot Password"
      heading="Forgot your password?"
      subheading="We'll email you a link to reset it"
      footer={
        <View className="flex-row justify-center">
          <AuthLink
            onPress={() => router.push({ pathname: '/(auth)/login' })}
            testID="forgot-login-link"
          >
            Back to log in
          </AuthLink>
        </View>
      }
    >
      <View className="gap-4">
        <TextInput
          placeholder="Email"
          value={email}
          onChangeText={(text) => {
            setEmail(text);
            if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
          }}
          autoCapitalize="none"
          autoComplete="email"
          textContentType="emailAddress"
          keyboardType="email-address"
          returnKeyType="send"
          onSubmitEditing={handleSendResetLink}
          editable={!isLoading}
          error={errors.email || undefined}
        />

        <Button
          size="lg"
          fullWidth
          className="mt-2"
          onPress={handleSendResetLink}
          loading={isLoading}
          testID="forgot-submit"
        >
          Send reset link
        </Button>
      </View>
    </AuthScreenLayout>
  );
}
